import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * CareRecord : famille "entrée de suivi individualisé".
 *
 * Entrée datée du suivi d'un être vivant ou d'une personne : soin, observation,
 * préconisation ou compte rendu de journée. Construite sur le modèle
 * "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("vaccination", "pesée",
 * "note de comportement") : il connaît la famille générique `careRecord`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app) : c'est ce que la fiche appelle "kind" (soin, observation,
 * préconisation, journal).
 *
 * Le sujet suivi est soit une fiche `asset` (animal, être vivant), soit une
 * fiche `contact` (personne) : `assetId` et `contactId` sont de simples ids,
 * l'un OU l'autre selon le sujet, jamais un import d'un autre schéma. Les autres
 * liens (document, measurement, booking, work-order) sont eux aussi de simples
 * ids optionnels.
 *
 * Sécurité / RGPD : dès que le sujet est humain (a fortiori mineur), le contenu
 * relève de la donnée de santé (RGPD art. 9). Le texte libre `details` porte ce
 * contenu sensible : il n'entre PAS dans le schéma v0, il ira dans un
 * sous-document privé (cf. WIP). En v0 la famille reste org-wide (même doctrine
 * qu'asset) ; l'accès fin (owner + rôle soignant/encadrant désigné, cloisonné
 * pour les sujets humains) viendra après, hors de ce schéma.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille careRecord (hors champs standard du socle). */
export const careRecordCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "soin", "observation", "préconisation", "journal"). */
  type: z.string().min(1),
  /** Sujet suivi quand c'est un `asset` (animal, être vivant). Optionnel (l'un des deux sujets). */
  assetId: z.string().min(1).optional(),
  /** Sujet suivi quand c'est une personne (référence `contact`). Optionnel (l'un des deux sujets). */
  contactId: z.string().min(1).optional(),
  /** Date à laquelle le soin / l'observation a été réalisé. Optionnel. */
  performedAt: z.date().optional(),
  /** Intervenant qui a réalisé l'entrée (référence `worker`). Optionnel. */
  practitionerWorkerId: z.string().min(1).optional(),
  // details : sensible (donnée de santé potentielle, RGPD art. 9), ira dans un sous-document privé (cf. WIP), hors v0.
  /** Prochaine échéance de suivi (prochain soin, prochain contrôle). Optionnel. */
  nextDueAt: z.date().optional(),
  /** Document rattaché (référence `document`). Optionnel. */
  documentId: z.string().min(1).optional(),
  /** Mesure rattachée (référence `measurement`). Optionnel. */
  measurementId: z.string().min(1).optional(),
  /** Réservation / rendez-vous rattaché (référence `booking`). Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Ordre de travail lié (référence `work-order`). Optionnel. */
  workOrderId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille careRecord (champs standard + champs métier). */
export const CareRecordSchema = z.object({
  ...baseEntityFields,
  ...careRecordCoreFields,
});

/** Type d'un careRecord. */
export type CareRecord = z.infer<typeof CareRecordSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const CareRecordCreateInputSchema = z.object({
  ...careRecordCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un careRecord. */
export type CareRecordCreateInput = z.infer<typeof CareRecordCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const CareRecordUpdateInputSchema = z
  .object({ ...careRecordCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un careRecord. */
export type CareRecordUpdateInput = z.infer<typeof CareRecordUpdateInputSchema>;
